import { useState, useRef, useEffect, useMemo } from 'react'
import { cn } from '@utils/cn'

export interface CatalogAutocompleteOption {
  costId: number
  label: string
  description: string
  hourlyRate?: number
  unitPrice?: number
}

interface CatalogAutocompleteInputProps {
  readonly label?: string
  readonly placeholder?: string
  readonly value: string
  readonly onValueChange: (value: string) => void
  readonly options: CatalogAutocompleteOption[]
  readonly onSelectOption?: (option: CatalogAutocompleteOption) => void
  readonly disabled?: boolean
  readonly className?: string
  readonly inputClassName?: string
  readonly emptyMessage?: string
}

export function CatalogAutocompleteInput({
  label,
  placeholder = 'Nome...',
  value,
  onValueChange,
  options,
  onSelectOption,
  disabled = false,
  className,
  inputClassName,
  emptyMessage = 'Nenhum resultado',
}: CatalogAutocompleteInputProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inputId = useRef(`catalog-ac-${Math.random().toString(36).slice(2, 9)}`).current

  const filteredOptions = useMemo(() => {
    const q = value.trim().toLowerCase()
    if (!q) return options
    return options.filter(
      (o) =>
        o.description.toLowerCase().includes(q) ||
        o.label.toLowerCase().includes(q)
    )
  }, [options, value])

  useEffect(() => {
    setHighlightedIndex(0)
  }, [value, filteredOptions.length])

  useEffect(() => {
    if (!isOpen || filteredOptions.length === 0) return
    const el = listRef.current?.children[highlightedIndex] as HTMLElement | undefined
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [highlightedIndex, isOpen, filteredOptions.length])

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current)
    }
  }, [])

  const closeDropdown = () => setIsOpen(false)

  const handleBlur = () => {
    blurTimeoutRef.current = setTimeout(() => {
      closeDropdown()
    }, 150)
  }

  const handleFocus = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current)
      blurTimeoutRef.current = null
    }
    if (!disabled) setIsOpen(true)
  }

  const handleSelect = (opt: CatalogAutocompleteOption) => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current)
      blurTimeoutRef.current = null
    }
    onValueChange(opt.description)
    onSelectOption?.(opt)
    closeDropdown()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || filteredOptions.length === 0) {
      if (e.key === 'ArrowDown' && filteredOptions.length > 0) {
        e.preventDefault()
        setIsOpen(true)
      }
      return
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex((i) => Math.min(i + 1, filteredOptions.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex((i) => Math.max(i - 1, 0))
        break
      case 'Enter':
        if (filteredOptions[highlightedIndex]) {
          e.preventDefault()
          handleSelect(filteredOptions[highlightedIndex])
        }
        break
      case 'Escape':
        e.preventDefault()
        closeDropdown()
        break
      default:
        break
    }
  }

  const showDropdown = isOpen && !disabled

  return (
    <div ref={containerRef} className={cn('relative w-full min-w-0', className)}>
      {label && (
        <label htmlFor={inputId} className="block text-xs font-medium text-secondary-700 mb-1">
          {label}
        </label>
      )}
      <input
        id={inputId}
        type="text"
        value={value}
        onChange={(e) => {
          onValueChange(e.target.value)
          setIsOpen(true)
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={showDropdown}
        className={cn(
          'w-full rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-900 focus:outline-none focus:ring-2',
          inputClassName
        )}
      />
      {showDropdown && (
        <ul
          ref={listRef}
          role="listbox"
          className="absolute z-50 mt-1 w-full max-h-48 overflow-auto rounded-lg border border-secondary-200 bg-white py-1 shadow-lg"
        >
          {filteredOptions.length === 0 ? (
            <li className="px-3 py-2 text-sm text-secondary-500">{emptyMessage}</li>
          ) : (
            filteredOptions.map((opt, i) => (
              <li
                key={opt.costId}
                role="option"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(opt)}
                onMouseEnter={() => setHighlightedIndex(i)}
                className={cn(
                  'cursor-pointer px-3 py-2 text-sm transition-colors',
                  i === highlightedIndex
                    ? 'bg-primary-50 text-primary-900'
                    : 'text-secondary-900 hover:bg-secondary-50'
                )}
              >
                {opt.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  )
}
